import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../redux";
import { fetchNotifications, setFilters, setPagination } from "../redux/reducers/notificationSlice";
import { View, Text, Button, Icon, Spinner } from "./base";
import { NotificationItem } from "./NotificationItem";

interface NotificationListProps {
  category?: string;
  showFilters?: boolean;
  showPagination?: boolean;
  limit?: number;
}

export const NotificationList = ({ category, showFilters = false, showPagination = true, limit = 50 }: NotificationListProps) => {
  const dispatch = useAppDispatch();
  const { notifications, isLoading, error, filters, pagination, unreadCount } = useAppSelector((state) => state.notifications);

  // Fetch notifications when filters or pagination changes
  useEffect(() => {
    dispatch(
      fetchNotifications({
        category,
        unread: filters.unread,
        page: pagination.page,
        limit,
      }),
    );
  }, [dispatch, category, filters.unread, pagination.page, limit]);

  const handleFilterChange = (filterName: "category" | "unread", value: any) => {
    dispatch(setFilters({ [filterName]: value }));
    dispatch(setPagination({ page: 1 })); // Reset to first page
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      dispatch(setPagination({ page: pagination.page - 1 }));
    }
  };

  const handleNextPage = () => {
    dispatch(setPagination({ page: pagination.page + 1 }));
  };

  if (isLoading && notifications.length === 0) {
    return (
      <View className="flex items-center justify-center py-12">
        <Spinner
          size={32}
          variant="brand"
        />
      </View>
    );
  }

  if (error) {
    return (
      <View className="p-6 text-center">
        <Text className="text-sm text-red-600 dark:text-red-400">Error loading notifications: {error}</Text>
      </View>
    );
  }

  return (
    <View className="flex flex-col h-full">
      {/* Filters */}
      {showFilters && (
        <View className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
          <View className="flex items-center gap-4">
            <Button
              type="button"
              variant={!filters.unread ? "brand" : "ghost"}
              size="sm"
              onClick={() => handleFilterChange("unread", undefined)}
            >
              All
            </Button>
            <Button
              type="button"
              variant={filters.unread === true ? "brand" : "ghost"}
              size="sm"
              onClick={() => handleFilterChange("unread", true)}
            >
              Unread ({unreadCount})
            </Button>
          </View>
        </View>
      )}

      {/* List of notifications */}
      <View className="flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <View className="p-12 text-center">
            <Icon
              name="notification"
              size={48}
              className="text-gray-300 dark:text-gray-600 mx-auto mb-4"
            />
            <Text className="text-lg font-medium text-gray-900 dark:text-white mb-2 block">No notifications</Text>
            <Text className="text-sm text-gray-500 dark:text-gray-400 block">You're all caught up!</Text>
          </View>
        ) : (
          <View>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.publicId}
                notification={notification}
              />
            ))}
          </View>
        )}
      </View>

      {/* Pagination */}
      {showPagination && notifications.length > 0 && (
        <View className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 flex items-center justify-between">
          <Text className="text-sm text-gray-600 dark:text-gray-400">Page {pagination.page}</Text>
          <View className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handlePrevPage}
              disabled={pagination.page === 1}
            >
              <Icon
                name="chevron-left"
                size={16}
              />
              Previous
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleNextPage}
              disabled={notifications.length < limit}
            >
              Next
              <Icon
                name="chevron-right"
                size={16}
              />
            </Button>
          </View>
        </View>
      )}
    </View>
  );
};
